import { apiClient } from '../utils/apiClient.js';
import { CONFIG } from '../config/config.js';
import { calculateChunkSize } from '../utils/chunkSizeCalculator.js';
import { saveResumeData, loadResumeData, clearResumeData } from '../utils/resumeStorage.js';
/** @typedef {import('../models/types').FileTransferRequest} FileTransferRequest */
/** @typedef {import('../models/types').FileTransferResultDto} FileTransferResultDto */

const API_BASE_URL = CONFIG.API_BASE_URL + '/api/FileTransfer';

// Security configuration - now using environment variables
// Currently configured for CSV files only as per business requirements
const { ALLOWED_FILE_TYPES, MAX_FILE_SIZE, MIN_FILE_SIZE } = CONFIG;

/**
 * Validate file for security purposes
 * @param {File} file 
 * @returns {Promise<{isValid: boolean, error?: string}>}
 */
export async function validateFile(file) {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    const maxSizeGB = (MAX_FILE_SIZE / (1024 * 1024 * 1024)).toFixed(1);
    return { isValid: false, error: `File too large. Maximum size allowed is ${maxSizeGB}GB` };
  }

  if (file.size < MIN_FILE_SIZE) {
    return { isValid: false, error: 'File is empty or corrupted' };
  }

  // Check file extension - currently only CSV files are allowed
  const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_FILE_TYPES.includes(fileExtension)) {
    return { isValid: false, error: `Invalid file format. Only CSV files accepted.` };
  }

  // Basic filename validation
  if (!/^[a-zA-Z0-9._\-\s()]+$/.test(file.name)) {
    return { isValid: false, error: 'Filename contains invalid characters. Only letters, numbers, spaces, dots, hyphens, underscores, and parentheses are allowed.' };
  }

  // Check for potential malicious files by reading first few bytes
  try {
    const firstChunk = file.slice(0, 1024);
    const arrayBuffer = await firstChunk.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    // Check for common malicious file signatures (especially important for CSV-only uploads)
    const firstBytes = Array.from(bytes.slice(0, 10)).map(b => b.toString(16).padStart(2, '0')).join('');

    // Block executable and potentially dangerous files
    const maliciousSignatures = [
      '4d5a', // PE executables (.exe, .dll)
      '7f454c46', // ELF executables
      '504b0304', // ZIP files (could contain executables)
      'cafebabe', // Java class files
      'ffd8ff', // JPEG files (not expected for CSV)
      '89504e47', // PNG files (not expected for CSV)
    ];

    for (const signature of maliciousSignatures) {
      if (firstBytes.startsWith(signature.toLowerCase())) {
        return { isValid: false, error: 'File content does not match expected CSV format' };
      }
    }
  } catch (error) {
    console.warn('File validation error:', error);
    // Don't fail validation due to read errors, but log for monitoring
  }

  return { isValid: true };
}

/**
 * Sanitize filename for security
 * @param {string} filename 
 * @returns {string}
 */
export function sanitizeFilename(filename) {
  return filename
    .replaceAll(/[<>:"/\\|?*]/g, '') // Remove invalid characters
    .replaceAll(/\s+/g, ' ') // Normalize whitespace
    .trim()
    .substring(0, 255); // Limit length
}

/**
 * Upload a file to the backend.
 * @param {FileTransferRequest} payload
 * @returns {Promise<FileTransferResultDto>}
 */
export async function uploadFile(payload) {
  const formData = new FormData();
  formData.append('File', payload.file);
  formData.append('OrganizationId', payload.organizationId);
  formData.append('UserId', payload.userId);

  const response = await apiClient.request('/api/FileTransfer', {
    method: 'POST',
    body: formData,
  });

  // Stop and surface validation errors the same way chunked upload does,
  // so UploadPage's catch block handles both paths identically.
  if (response.validationPassed === false) {
    const validationError = new Error(response.message || 'CSV validation failed');
    validationError.isValidationError = true;
    validationError.validationErrors = response.validationErrors || [];
    validationError.validationWarnings = response.validationWarnings || [];
    validationError.hasContentErrors = response.hasContentErrors;
    validationError.totalRows = response.totalRows;
    validationError.invalidRows = response.invalidRows;
    validationError.chunkIndex = null; // not applicable for direct upload
    throw validationError;
  }

  return {
    success: response.status === 'Success',
    status: response.status,
    message: response.message,
    uploadId: response.uploadId,
    fileName: response.fileName,
    validationWarnings: response.validationWarnings || [],
    totalRows: response.totalRows,
    validRows: response.validRows,
    invalidRows: response.invalidRows
  };
}

/**
 * Create form data for chunk upload
 * @param {Blob} chunk - File chunk
 * @param {string} uploadId - Upload identifier
 * @param {number} chunkIndex - Current chunk index
 * @param {number} totalChunks - Total number of chunks
 * @param {string} fileName - Original filename
 * @param {Object} payload - Upload payload containing organizationId and userId
 * @param {number} startRowNumber - Starting row number of the first record in this chunk (1-based; all rows are data rows)
 * @returns {FormData} Prepared form data
 */
function createChunkFormData(chunk, uploadId, chunkIndex, totalChunks, fileName, payload, startRowNumber) {
  const formData = new FormData();
  formData.append('ChunkFile', chunk);
  formData.append('UploadId', uploadId);
  formData.append('ChunkIndex', chunkIndex.toString());
  formData.append('TotalChunks', totalChunks.toString());
  formData.append('FileName', fileName);
  formData.append('OrganizationId', payload.organizationId.toString());
  formData.append('UserId', payload.userId.toString());
  formData.append('StartRowNumber', startRowNumber.toString());
  return formData;
}

/**
 * Count the number of rows (lines) in a chunk by counting newline characters.
 * All rows are data rows (no header assumption).
 * @param {Blob} chunk - File chunk
 * @returns {Promise<number>} Total number of rows in the chunk
 */
async function countRowsInChunk(chunk) {
  const text = await chunk.text();
  return (text.match(/\n/g) || []).length;
}

/**
 * Calculate progress metrics for upload
 * @param {number} chunkIndex - Current chunk index
 * @param {number} totalChunks - Total number of chunks
 * @param {number} uploadedBytes - Bytes uploaded so far
 * @param {number} totalBytes - Total file size
 * @param {number} startTime - Upload start timestamp
 * @returns {Object} Progress metrics
 */
function calculateProgressMetrics(chunkIndex, totalChunks, uploadedBytes, totalBytes, startTime) {
  const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
  const totalDuration = (Date.now() - startTime) / 1000; // seconds
  const uploadSpeed = totalDuration > 0 ? uploadedBytes / totalDuration : 0; // bytes per second
  const remainingBytes = totalBytes - uploadedBytes;
  const estimatedTimeRemaining = uploadSpeed > 0 ? remainingBytes / uploadSpeed : 0; // seconds

  return {
    progress,
    uploadSpeed: Math.round(uploadSpeed / 1024), // KB/s
    estimatedTimeRemaining: Math.round(estimatedTimeRemaining)
  };
}

/**
 * Calculate row-aligned chunk boundaries for CSV files.
 * Ensures each chunk ends at a complete CSV row (newline boundary) so that
 * the last row of every chunk is never truncated mid-row during validation.
 * @param {File} file - The file to split
 * @param {number} chunkSize - Target chunk size in bytes
 * @returns {Promise<Array<{start: number, end: number}>>} Array of chunk boundaries
 */
async function calculateRowAlignedChunkBoundaries(file, chunkSize) {
  const boundaries = [];
  let currentStart = 0;

  while (currentStart < file.size) {
    let nominalEnd = Math.min(currentStart + chunkSize, file.size);

    // For non-final chunks, extend to the next newline so no row is split
    if (nominalEnd < file.size) {
      const lookaheadSize = Math.min(4096, file.size - nominalEnd);
      if (lookaheadSize > 0) {
        const lookaheadBlob = file.slice(nominalEnd, nominalEnd + lookaheadSize);
        const text = await lookaheadBlob.text();
        const newlineIndex = text.indexOf('\n');
        if (newlineIndex !== -1) {
          nominalEnd = nominalEnd + newlineIndex + 1; // include the '\n'
        }
        // If no newline found in lookahead, fall through with nominalEnd as-is (extremely long row edge case)
      }
    }

    boundaries.push({ start: currentStart, end: nominalEnd });
    currentStart = nominalEnd;
  }

  return boundaries;
}

/**
 * Upload a large file using chunked upload.
 * @param {FileTransferRequest} payload
 * @param {Function} onProgress - Progress callback with detailed progress data
 * @returns {Promise<FileTransferResultDto>}
 */
export async function uploadChunkedFile(payload, onProgress) {
  const { file } = payload;
  const uploadId = `upload_${Date.now()}_${crypto?.randomUUID?.() || Math.random().toString(36).substring(2, 15)}`;

  // Calculate optimal chunk size
  const chunkSize = await calculateChunkSize(file.size);

  // Calculate row-aligned boundaries so each chunk contains only complete CSV rows
  const chunkBoundaries = await calculateRowAlignedChunkBoundaries(file, chunkSize);
  const totalChunks = chunkBoundaries.length;

  console.log(`Starting chunked upload: ${file.name}, Size: ${(file.size / (1024 * 1024)).toFixed(2)} MB, Chunks: ${totalChunks}, Chunk Size: ${(chunkSize / (1024 * 1024)).toFixed(2)} MB`);

  // Check for existing resume data
  const resumeData = loadResumeData(uploadId);
  let startChunk = resumeData ? resumeData.lastCompletedChunk + 1 : 0;

  const startTime = Date.now();
  let uploadedBytes = startChunk > 0 ? chunkBoundaries[startChunk - 1].end : 0;

  // StartRowNumber tracks the absolute row number of the first data row in each chunk (1-based).
  // All rows are treated as data rows (no header assumption).
  // Restored from resume data when resuming a partial upload.
  let currentStartRowNumber = resumeData?.startRowNumber ?? 1;

  try {
    for (let chunkIndex = startChunk; chunkIndex < totalChunks; chunkIndex++) {
      const { start, end } = chunkBoundaries[chunkIndex];
      const chunk = file.slice(start, end);

      // Count rows in this chunk so we can advance StartRowNumber for the next chunk.
      const rowsInChunk = await countRowsInChunk(chunk);

      const formData = createChunkFormData(chunk, uploadId, chunkIndex, totalChunks, file.name, payload, currentStartRowNumber);

      const response = await apiClient.request('/api/FileTransfer/upload-chunk', {
        method: 'POST',
        body: formData,
      });

      // Stop upload immediately if this chunk failed validation
      if (response.validationPassed === false) {
        console.error(`Chunk ${chunkIndex} failed validation:`, response.validationErrors);
        clearResumeData(uploadId);
        const validationError = new Error(response.message || 'CSV validation failed during upload');
        validationError.isValidationError = true;
        validationError.validationErrors = response.validationErrors || [];
        validationError.validationWarnings = response.validationWarnings || [];
        validationError.hasContentErrors = response.hasContentErrors;
        validationError.totalRows = response.totalRows;
        validationError.invalidRows = response.invalidRows;
        validationError.chunkIndex = chunkIndex;
        throw validationError;
      }

      const chunkBytes = end - start;
      uploadedBytes += chunkBytes;

      // Advance StartRowNumber for the next chunk.
      // All rows in each chunk are data rows.
      currentStartRowNumber += rowsInChunk;

      // Calculate progress metrics
      const { progress, uploadSpeed, estimatedTimeRemaining } = calculateProgressMetrics(chunkIndex, totalChunks, uploadedBytes, file.size, startTime);

      // Save resume data
      saveResumeData(uploadId, {
        fileName: file.name,
        fileSize: file.size,
        totalChunks,
        lastCompletedChunk: chunkIndex,
        startRowNumber: currentStartRowNumber,
        organizationId: payload.organizationId,
        userId: payload.userId,
        timestamp: Date.now()
      });

      // Call progress callback
      if (onProgress) {
        onProgress({
          progress,
          phase: 'uploading',
          uploadSpeed,
          estimatedTimeRemaining,
          currentChunk: chunkIndex + 1,
          totalChunks
        });
      }

      // Check if upload is complete
      if (response.isComplete) {
        console.log('Chunked upload completed successfully');
        clearResumeData(uploadId);

        if (onProgress) {
          onProgress({
            progress: 100,
            phase: 'complete',
            uploadSpeed,
            estimatedTimeRemaining: 0,
            currentChunk: totalChunks,
            totalChunks
          });
        }

        return {
          success: true,
          status: response.status || 'Success',
          message: response.message || 'File uploaded successfully',
          uploadId: response.finalUploadId || uploadId,
          fileName: response.fileName || file.name,
          validationWarnings: response.validationWarnings || [],
          totalRows: response.totalRows,
          validRows: response.validRows,
          invalidRows: response.invalidRows
        };
      }
    }

    // This shouldn't happen if the backend is working correctly
    throw new Error('Upload completed all chunks but backend did not confirm completion');

  } catch (error) {
    console.error('Error during chunked upload:', error);

    // Don't clear resume data on error - allow for resume
    if (onProgress) {
      onProgress({
        progress: Math.round((startChunk / totalChunks) * 100),
        phase: 'error',
        uploadSpeed: 0,
        estimatedTimeRemaining: 0,
        currentChunk: startChunk,
        totalChunks
      });
    }

    throw error;
  }
}

/**
 * Check if there's enough space available for the file upload.
 * @param {number} fileSizeBytes - Size of file to upload in bytes
 * @returns {Promise<{hasSpace: boolean, availableSpace: number, message: string}>}
 */
export async function checkAvailableSpace(fileSizeBytes) {
  try {
    const response = await apiClient.get(`/api/FileTransfer/check-space?fileSizeBytes=${fileSizeBytes}`);
    return response;
  } catch (error) {
    console.error('Error checking available space:', error);
    return {
      hasSpace: false,
      availableSpace: 0,
      message: 'Failed to check available storage space. Please try again later.'
    };
  }
}

/**
 * Get pending uploads from resume storage.
 * @returns {Array} Array of pending upload metadata
 */
export function getPendingUploads() {
  const resumeKey = CONFIG.RESUME_STORAGE_KEY;
  try {
    const stored = sessionStorage.getItem(resumeKey);
    if (stored) {
      const data = JSON.parse(stored);
      const now = Date.now();
      const expiryMs = CONFIG.RESUME_EXPIRY_HOURS * 60 * 60 * 1000;

      // Filter out expired entries
      return Object.entries(data)
        .filter(([, uploadData]) => (now - uploadData.timestamp) < expiryMs)
        .map(([uploadId, uploadData]) => ({
          uploadId,
          ...uploadData
        }));
    }
    return [];
  } catch (error) {
    console.error('Error getting pending uploads:', error);
    return [];
  }
}

/**
 * Clear a pending upload from resume storage.
 * @param {string} uploadId - Upload ID to clear
 */
export function clearPendingUpload(uploadId) {
  clearResumeData(uploadId);
}

/**
 * Resume an upload from where it left off.
 * @param {string} uploadId - Upload ID to resume
 * @param {File} file - File to resume uploading
 * @param {Object} payload - Upload payload (organizationId, userId)
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<FileTransferResultDto>}
 */
export async function resumeUpload(uploadId, file, payload, onProgress) {
  console.log(`Resuming upload: ${uploadId}`);

  // Create a new payload with the file and resume the chunked upload
  const resumePayload = {
    ...payload,
    file
  };

  return await uploadChunkedFile(resumePayload, onProgress);
}



